import { createSlice } from '@reduxjs/toolkit'
import { requests } from '../../../core/ajax'
import { feedback } from '../../../core/feedback'

const getTipoFromStates = (isInBlacklist, isInWhitelist) => {
  if (isInBlacklist && isInWhitelist) return 'both'
  if (isInBlacklist) return 'black'
  if (isInWhitelist) return 'white'
  return 'none'
}

const calculatePagination = (totalItems, itemsPerPage, currentPage) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  return { totalPages, startIndex, endIndex }
}

const getPaginatedData = (data, startIndex, endIndex) => {
  return data.slice(startIndex, endIndex)
}

const emailManagementSlice = createSlice({
  name: 'emailManagement',
  initialState: {
    isLoading: false,
    data: [],
    allData: [],
    search: {},
    pagination: {},
  },
  reducers: {
    setLoading: (state, { payload }) => {
      state.isLoading = payload
    },
    setEmailsData: (state, { payload }) => {
      state.data = payload.data
      if (payload.allData !== undefined) {
        state.allData = payload.allData
      }
      if (payload.search) {
        state.search = payload.search
      }
      if (payload.pagination) {
        state.pagination = payload.pagination
      }
      state.isLoading = false
    },
  },
})

export const emailManagementReducer = emailManagementSlice.reducer

export const { setLoading, setEmailsData } = emailManagementSlice.actions

export const setEmailManagement = (searchParams) => (dispatch, getState) => {
  const currentState = getState().emailManagement
  const currentNumXPagina = currentState.search?._num_x_pagina || 5

  const _pagina = parseInt(searchParams._pagina, 10) || 1
  const _num_x_pagina =
    parseInt(searchParams._num_x_pagina, 10) || parseInt(currentNumXPagina, 10) || 5
  const { black, white, _search } = searchParams
  const { get } = requests

  const filterEmailsBySearch = (emails, searchTerm) => {
    if (!searchTerm || searchTerm.trim() === '') {
      return emails
    }
    const search = searchTerm.toLowerCase().trim()
    return emails.filter((email) => email.email && email.email.toLowerCase().includes(search))
  }

  const filterEmailsByType = (emails, blackFilter, whiteFilter) => {
    const blackFilterBool = blackFilter === 'true'
    const whiteFilterBool = whiteFilter === 'true'

    if (!blackFilterBool && !whiteFilterBool) {
      return emails
    }

    return emails.filter((email) => {
      switch (true) {
        case blackFilterBool && !whiteFilterBool:
          return email.isInBlacklist
        case whiteFilterBool && !blackFilterBool:
          return email.isInWhitelist
        case blackFilterBool && whiteFilterBool:
          return email.isInBlacklist || email.isInWhitelist
        default:
          return true
      }
    })
  }

  const hasSearch = _search && _search.trim() !== ''

  if (currentState.allData && currentState.allData.length > 0) {
    const searchFiltered = filterEmailsBySearch(currentState.allData, _search)
    const typeFiltered = filterEmailsByType(searchFiltered, black, white)
    const { totalPages, startIndex, endIndex } = calculatePagination(
      typeFiltered.length,
      _num_x_pagina,
      _pagina
    )
    const paginatedData = getPaginatedData(typeFiltered, startIndex, endIndex)

    dispatch(
      setEmailsData({
        data: paginatedData,
        allData: currentState.allData,
        search: { ...searchParams, _pagina, _num_x_pagina },
        pagination: {
          total_paginas: totalPages,
          total_emails: typeFiltered.length,
        },
      })
    )
    return
  }

  const searchParams_api = hasSearch ? { email: _search } : {}

  const configs = [
    get(`/gestion_email`, { params: { tipo: 'white', ...searchParams_api } }),
    get(`/gestion_email`, { params: { tipo: 'black', ...searchParams_api } }),
  ]

  dispatch(setLoading(true))

  Promise.all(configs)
    .then(([whiteResponse, blackResponse]) => {
      const whiteData = whiteResponse.data
      const blackData = blackResponse.data

      if (!whiteData && !blackData) {
        console.error('Error recuperando emails')
        dispatch(setLoading(false))
        return
      }

      const emailMap = new Map()

      const whiteEmails = whiteData?.white_mails || []
      whiteEmails.forEach((item) => {
        emailMap.set(item.email, {
          ...item,
          whitelistId: item.id,
          blacklistId: null,
          isInWhitelist: item.active === true,
          isInBlacklist: false,
          tipo: item.active === true ? 'white' : 'none',
          motive: item.motive || '',
        })
      })

      const blackEmails = blackData?.black_mails || []
      blackEmails.forEach((item) => {
        if (emailMap.has(item.email)) {
          const existingItem = emailMap.get(item.email)
          const isInWhitelist = existingItem.isInWhitelist
          const isInBlacklist = item.active === true
          // Si whitelist está activa, la blacklist se muestra como no activa
          const displayBlacklist = isInWhitelist ? false : isInBlacklist
          emailMap.set(item.email, {
            ...existingItem,
            blacklistId: item.id,
            isInBlacklist: displayBlacklist,
            tipo: isInWhitelist ? 'white' : (displayBlacklist ? 'black' : 'none'),
            motive: item.motive || existingItem.motive || '',
          })
        } else {
          emailMap.set(item.email, {
            ...item,
            whitelistId: null,
            blacklistId: item.id,
            isInWhitelist: false,
            isInBlacklist: item.active === true,
            tipo: item.active === true ? 'black' : 'none',
            motive: item.motive || '',
          })
        }
      })

      const combinedData = Array.from(emailMap.values()).map((item, index) => ({
        ...item,
        id: `email_${index}`,
        uniqueKey: item.email,
      }))

      const searchFiltered = filterEmailsBySearch(combinedData, _search)
      const typeFiltered = filterEmailsByType(searchFiltered, black, white)
      const { totalPages, startIndex, endIndex } = calculatePagination(
        typeFiltered.length,
        _num_x_pagina,
        _pagina
      )
      const paginatedData = getPaginatedData(typeFiltered, startIndex, endIndex)

      dispatch(
        setEmailsData({
          data: paginatedData,
          allData: combinedData,
          search: { ...searchParams, _pagina, _num_x_pagina },
          pagination: {
            total_paginas: totalPages,
            total_emails: typeFiltered.length,
          },
        })
      )
    })
    .catch((error) => {
      console.error('Error recuperando emails', error)
      dispatch(setLoading(false))
    })
}

export const setToggleEmailStatus = (row, emails, setIsLoading) => (dispatch, getState) => {
  setIsLoading(true)
  const { putById } = requests
  const blackOrWhiteList = row.tipo === 'black' ? 'white' : 'black'

  const idToUse = blackOrWhiteList === 'black' ? row.blacklistId : row.whitelistId

  if (!idToUse) {
    console.error(`No se encontró ${blackOrWhiteList}listId para el email ${row.email}`)
    setIsLoading(false)
    return
  }

  const {
    isInBlacklist,
    isInWhitelist,
    tableData,
    whitelistId,
    blacklistId,
    dg_ts_insert,
    dg_ts_update,
    ...cleanRow
  } = row
  const payload = { tipo: blackOrWhiteList, ...cleanRow }

  putById('/gestion_email', idToUse, payload, { params: { tipo: blackOrWhiteList } })
    .then(({ data }) => {
      const currentState = getState().emailManagement
      const updatedType = data.tipo === true || data.tipo === 'true' ? 'white' : 'black'

      const newEmails = emails.slice()
      newEmails[row.tableData.id] = {
        ...newEmails[row.tableData.id],
        tipo: updatedType,
      }

      const newAllData = currentState.allData.map((item) =>
        item.uniqueKey === row.uniqueKey ? { ...item, tipo: updatedType } : item
      )

      dispatch(
        setEmailsData({
          data: newEmails,
          allData: newAllData,
        })
      )
      setIsLoading(false)
    })
    .catch(({ response }) => {
      feedback('error', response.data.message || 'No se han podido guardar los cambios')
      setIsLoading(false)
    })
}

export const setToggleBlacklistStatus =
  (row, listType, setIsLoading, onRequireMotive) => (dispatch, getState) => {
    setIsLoading(true)
    const { putById, post } = requests
    const currentState = getState().emailManagement
    const empresa_id = getState().global['empresa_id']

    const currentStatus = listType === 'black' ? row.isInBlacklist : row.isInWhitelist
    const newStatus = !currentStatus

    // Identificar la lista opuesta
    const oppositeListType = listType === 'black' ? 'white' : 'black'
    const oppositeCurrentStatus = listType === 'black' ? row.isInWhitelist : row.isInBlacklist
    const oppositeIdToUse = listType === 'black' ? row.whitelistId : row.blacklistId

    const idToUse = listType === 'black' ? row.blacklistId : row.whitelistId

    const shouldCreateNew = !idToUse && newStatus

    if (!idToUse && !newStatus) {
      console.log(
        `No se puede desactivar ${listType} para ${row.email} porque no existe en la lista`
      )
      setIsLoading(false)
      return
    }

    if (listType === 'black' && newStatus && !row.motive && onRequireMotive) {
      setIsLoading(false)
      onRequireMotive(row, listType)
      return
    }

    // Si estamos activando una lista y la opuesta está activa, necesitamos desactivarla
    const shouldDeactivateOpposite = newStatus && oppositeCurrentStatus && oppositeIdToUse

    const newEmails = currentState.data.map((item) =>
      item.uniqueKey === row.uniqueKey
        ? {
            ...item,
            [listType === 'black' ? 'isInBlacklist' : 'isInWhitelist']: newStatus,
            // Si estamos activando esta lista, desactivar la opuesta
            [oppositeListType === 'black' ? 'isInBlacklist' : 'isInWhitelist']: shouldDeactivateOpposite ? false : item[oppositeListType === 'black' ? 'isInBlacklist' : 'isInWhitelist'],
            tipo: shouldDeactivateOpposite 
              ? (newStatus ? listType : 'none')
              : getTipoFromStates(
                  listType === 'black' ? newStatus : item.isInBlacklist,
                  listType === 'white' ? newStatus : item.isInWhitelist
                ),
            ...(listType === 'black' && !newStatus
              ? { motive: '' }
              : row.motive !== undefined && { motive: row.motive }),
          }
        : item
    )

    const newAllData = currentState.allData.map((item) =>
      item.uniqueKey === row.uniqueKey
        ? {
            ...item,
            [listType === 'black' ? 'isInBlacklist' : 'isInWhitelist']: newStatus,
            // Si estamos activando esta lista, desactivar la opuesta
            [oppositeListType === 'black' ? 'isInBlacklist' : 'isInWhitelist']: shouldDeactivateOpposite ? false : item[oppositeListType === 'black' ? 'isInBlacklist' : 'isInWhitelist'],
            tipo: shouldDeactivateOpposite 
              ? (newStatus ? listType : 'none')
              : getTipoFromStates(
                  listType === 'black' ? newStatus : item.isInBlacklist,
                  listType === 'white' ? newStatus : item.isInWhitelist
                ),
            ...(listType === 'black' && !newStatus
              ? { motive: '' }
              : row.motive !== undefined && { motive: row.motive }),
          }
        : item
    )

    dispatch(
      setEmailsData({
        data: newEmails,
        allData: newAllData,
        search: currentState.search,
        pagination: currentState.pagination,
      })
    )

    const {
      isInBlacklist,
      isInWhitelist,
      tipo,
      tableData,
      whitelistId,
      blacklistId,
      dg_ts_insert,
      dg_ts_update,
      ...emailData
    } = row
    const payload = {
      ...emailData,
      email: row.email,
      active: newStatus,
      motive: listType === 'black' && !newStatus ? '' : row.motive || '',
      tipo: listType,
      empresa_id: Number(empresa_id),
    }

    const apiCall = shouldCreateNew
      ? post('/gestion_email', payload)
      : putById('/gestion_email', idToUse, payload, { params: { tipo: listType } })

    // Preparar la llamada para desactivar la lista opuesta si es necesario
    const apiCalls = [apiCall]
    
    if (shouldDeactivateOpposite) {
      const oppositePayload = {
        ...emailData,
        email: row.email,
        active: false,
        motive: oppositeListType === 'black' ? '' : row.motive || '',
        tipo: oppositeListType,
        empresa_id: Number(empresa_id),
      }
      apiCalls.push(
        putById('/gestion_email', oppositeIdToUse, oppositePayload, { params: { tipo: oppositeListType } })
      )
    }

    Promise.all(apiCalls)
      .then(([response, oppositeResponse]) => {
        if (shouldCreateNew && response.data) {
          const newId = response.data.id
          const latestState = getState().emailManagement

          const updatedEmails = latestState.data.map((item) =>
            item.uniqueKey === row.uniqueKey
              ? {
                  ...item,
                  [listType === 'black' ? 'blacklistId' : 'whitelistId']: newId,
                  motive: payload.motive,
                }
              : item
          )

          const updatedAllData = latestState.allData.map((item) =>
            item.uniqueKey === row.uniqueKey
              ? {
                  ...item,
                  [listType === 'black' ? 'blacklistId' : 'whitelistId']: newId,
                  motive: payload.motive,
                }
              : item
          )

          dispatch(
            setEmailsData({
              data: updatedEmails,
              allData: updatedAllData,
              search: latestState.search,
              pagination: latestState.pagination,
            })
          )
        }

        setIsLoading(false)
      })
      .catch((error) => {
        const revertedEmails = currentState.data.map((item) =>
          item.uniqueKey === row.uniqueKey
            ? {
                ...item,
                [listType === 'black' ? 'isInBlacklist' : 'isInWhitelist']: currentStatus,
                [oppositeListType === 'black' ? 'isInBlacklist' : 'isInWhitelist']: oppositeCurrentStatus,
                tipo: getTipoFromStates(
                  listType === 'black' ? currentStatus : oppositeCurrentStatus,
                  listType === 'white' ? currentStatus : oppositeCurrentStatus
                ),
              }
            : item
        )

        const revertedAllData = currentState.allData.map((item) =>
          item.uniqueKey === row.uniqueKey
            ? {
                ...item,
                [listType === 'black' ? 'isInBlacklist' : 'isInWhitelist']: currentStatus,
                [oppositeListType === 'black' ? 'isInBlacklist' : 'isInWhitelist']: oppositeCurrentStatus,
                tipo: getTipoFromStates(
                  listType === 'black' ? currentStatus : oppositeCurrentStatus,
                  listType === 'white' ? currentStatus : oppositeCurrentStatus
                ),
              }
            : item
        )

        dispatch(
          setEmailsData({
            data: revertedEmails,
            allData: revertedAllData,
            search: currentState.search,
            pagination: currentState.pagination,
          })
        )

        feedback('error', error?.response?.data?.message || 'No se han podido guardar los cambios')
        setIsLoading(false)
      })
  }

export const createEmailException = (emailData, setIsLoading, close) => (dispatch, getState) => {
  setIsLoading(true)
  const { post } = requests

  const promises = []

  if (emailData.isInWhitelist) {
    const whitePayload = {
      ...emailData,
      tipo: 'white',
      motive: emailData.motive || '',
    }
    promises.push(post('/gestion_email', whitePayload))
  }

  if (emailData.isInBlacklist) {
    const blackPayload = {
      ...emailData,
      tipo: 'black',
      motive: emailData.motive || '',
    }
    promises.push(post('/gestion_email', blackPayload))
  }

  Promise.all(promises)
    .then(() => {
      feedback('success', 'Excepción creada correctamente')

      dispatch(
        setEmailsData({
          data: [],
          allData: [],
          search: {},
          pagination: {},
        })
      )

      const currentState = getState().emailManagement
      const fullSearchParams = {
        ...currentState.search,
        _pagina: 1,
      }
      dispatch(setEmailManagement(fullSearchParams))
      setIsLoading(false)
      close()
    })
    .catch((error) => {
      feedback('error', error?.response?.data?.message || 'No se ha podido crear la excepción')
      setIsLoading(false)
    })
}

export const setToggleBlacklistStatusWithMotive =
  (row, listType, motive, setIsLoading) => (dispatch, getState) => {
    const rowWithMotive = { ...row, motive: motive || '' }

    return dispatch(setToggleBlacklistStatus(rowWithMotive, listType, setIsLoading, null))
  }
